import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Button, Card, Spinner } from 'react-bootstrap';
import { doc, getDoc, collection, getDocs, query, where, orderBy } from 'firebase/firestore'; // Import Firestore functions, including 'where'
import { db } from '../firebase';
import '../css/Home.css';
import ProfileModal from './ProfileModal';
import {Helmet} from 'react-helmet';
// import startConfetti from './startConfetti'; // Import the confetti function
import AdsBanner from './AdsBanner';
import { Tab, Nav } from 'react-bootstrap'; // Import Bootstrap Tab and Nav components
import { setDoc } from 'firebase/firestore';

const YesterdayLeaderboard = lazy(() => import('./YesterdayLeaderboard'));
const EventLeaderboard = lazy(() => import('./EventLeaderboard'));
const dynamicTitle = `Home - ${window.defaultTitle}`;
const Home = ({ playerData, setPlayerData }) => {
  const navigate = useNavigate();
  const [quizTitle, setQuizTitle] = useState('');
  const [quizPlayedToday, setQuizPlayedToday] = useState(false);
  const [totalPoints, setTotalPoints] = useState(0);
  const [rank, setRank] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [streak, setStreak] = useState(0); // State for streak
  
  const fetchQuizTitle = useCallback(async () => {
    const today = new Date().toISOString().split('T')[0];
    const todayQuery = query(collection(db, 'quizzes'), where('publicationDate', '==', today));
    const todaySnapshot = await getDocs(todayQuery);

    if (!todaySnapshot.empty) {
        const quizData = todaySnapshot.docs[0].data();
        setQuizTitle(quizData.quizTitle);
    } else {
        // Fetch random quiz from previous dates
        const previousQuery = query(
            collection(db, 'quizzes'),
            where('publicationDate', '<', today),
            orderBy('publicationDate', 'desc')
        );
        const previousSnapshot = await getDocs(previousQuery);

        if (!previousSnapshot.empty) {
            // Pick a random quiz from previous dates
            const randomQuiz =
                previousSnapshot.docs[Math.floor(Math.random() * previousSnapshot.docs.length)];
            const quizData = randomQuiz.data();

            // Update the quiz title to "today's date Quiz"
            const todayDateName = `${new Date(today).toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'long',
            })} Quiz`;

            setQuizTitle(todayDateName);

            // Optional: Update today's quiz title in Firebase
            const todayDocRef = doc(collection(db, 'quizzes'), today);
            await setDoc(todayDocRef, {
                ...quizData,
                quizTitle: todayDateName, // Update title to today's date
                publicationDate: today, // Set today's date
            });
        } else {
            setQuizTitle('New quiz available soon');
        }
    }
}, []);

  useEffect(() => {
    fetchQuizTitle();
    // startConfetti(); // Trigger the confetti when the component mounts
  }, [fetchQuizTitle]);

  const fetchPlayerData = useCallback(async () => {
    if (!playerData.id) return;
    const playerRef = doc(db, 'players', playerData.id);
    const docSnap = await getDoc(playerRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      setTotalPoints(data.totalPoints || 0);

      const today = new Date().toISOString().split('T')[0];
      setQuizPlayedToday((data.quizPlayedToday || []).includes(today));
    }
  }, [playerData.id]);

  useEffect(() => {
    fetchPlayerData();
    fetchStreak();
  }, [fetchPlayerData]);

  const fetchStreak = async () => {
    try {
      const playerId = playerData.id;
      const playerRef = doc(db, 'players', playerId);
      const playerSnap = await getDoc(playerRef);
      const playedDates = playerSnap.data().quizPlayedToday || [];
      
      const updatedStreak = calculateStreak(playedDates);

      setStreak(updatedStreak);

      return updatedStreak; // Return streak value
    } catch (error) {
      console.error('Error fetching Streak And WData:', error);
      return 0; // Return 0 to indicate failure
    }
  };

  const calculateStreak = (dates) => {
    // Sort dates in descending order
    dates.sort((a, b) => new Date(b) - new Date(a));
  
    let streak = 0;
    let lastDate = new Date(dates[0]); // Start with the most recent date
  
    for (let i = 0; i < dates.length; i++) {
      const currentDate = new Date(dates[i]).toISOString().split('T')[0];
      
      if (i === 0) {
        streak = 1; // Initialize streak with the first date
      } else {
        const previousDate = new Date(lastDate);
        previousDate.setDate(previousDate.getDate() - 1);
  
        if (currentDate === previousDate.toISOString().split('T')[0]) {
          streak++;
          lastDate = new Date(currentDate);
        } else {
          break; // Streak is broken
        }
      }
    }
  
    return streak;
  };
  


  const fetchLeaderboard = useCallback(async () => {
    const playersRef = collection(db, 'players');
    const q = query(playersRef, orderBy('totalPoints', 'desc'));
    const querySnapshot = await getDocs(q);

    const leaderboard = [];
    querySnapshot.forEach((doc) => {
      leaderboard.push({ id: doc.id, ...doc.data() });
    });

    const currentPlayerRank = leaderboard.findIndex(player => player.id === playerData.id) + 1;
    setRank(currentPlayerRank);
  }, [playerData.id]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const handleOpenProfileModal = () => setShowProfileModal(true);
  const handleCloseProfileModal = () => setShowProfileModal(false);

  const handlePlayerDataUpdate = (updatedData) => {
    setPlayerData(updatedData);
  };
  
  return (
    <div>
      <Container className="d-flex align-items-center flex-column" style={{ height: '100vh' }}>
        <Helmet>
            <title>{dynamicTitle}</title>
        </Helmet>
        <header className='Header-home'>
          <div className='home-logo-sec'>
            <div className='left-nav-logo'>
              <img src='https://eduplayzone.in/image/epz-favicon.png' alt="eduplayzone quiz zone" />
              <h4 className='logo-text'>Quiz zone</h4>
            </div>
            <div>
              <button type="button" className="btn btn-secondary btn-sm edit-p-b" onClick={handleOpenProfileModal}>
                <i className="fa-solid fa-user-pen"></i> Edit Profile
              </button>
            </div>
          </div>
          <div className='user-d-h'>
            <div className='header-left'>
              <h3>Hi, {playerData.name.split(" ")[0]} </h3>
              <span className='header-subText'>Let’s make learning fun!</span>
            </div>
            <div className='header-right'>
              <img
                src={playerData.avatar}
                width="30"
                height="30"
                className="d-inline-block align-top rounded-circle"
                alt="Player Avatar"
              />
            </div>
          </div>
          <div className='user-performence col'>
            <div className='user-p row'>
            <div className='col align-items-center d-flex justify-content-center'>
              <img className='rankImg-home' style={{ height: '46px' }} src='/quiz-zone/image/trophy.png' alt="Trophy" />
              <div className='ml-2'>
                <span>Ranking</span>
                <h4>#{rank || ''}</h4>
              </div>
            </div>
            <div className='col align-items-center d-flex justify-content-center VL'>
              <img className='pointImg-home' style={{ height: '46px' }} src='/quiz-zone/image/coin.png' alt="Coin" />
              <div className='ml-2 align-items-center'>
                <span>Points</span>
                <h4>{totalPoints}</h4>
              </div>
            </div>
            </div>
            <span className='winStreak_text'>{streak}-Day streak</span>

          </div>
        </header>
        <AdsBanner />
       
       {/*Rocket*/}
        {/* <img className='rocket' style={{ height: '80px' }} src='/quiz-zone/image/rocket.png' alt="space day india"/> */}
        {/* Astronaut*/}
        {/* <img className='astronaut' style={{ height: '80px' }} src='/quiz-zone/image/astronaut.png' alt="space day india"/> */}
        {/* satelite*/}
        {/* <img className='satelite' style={{ height: '80px' }} src='/quiz-zone/image/satelite.png' alt="space day india"/> */}
       
        <h5 className='d-flex align-items-center w-100'>Let's play</h5>
        <section className='Quiz-text'>
          <span><i class="fa-solid fa-bell"></i> Daily</span>
          <h5>Welcome to the Daily Quiz Challenge!</h5>
            {/* <p>Join EduPlayZone's Space Day Quiz to honor ISRO's achievements. Test your knowledge, climb the leaderboard, and journey to the Moon with daily quizzes leading up to August 23rd. Celebrate National Space Day with us!</p> */}
            <p>Join the daily quiz challenge! Test your knowledge, rise up the leaderboard, and win exciting prizes. Stay engaged with fresh, daily quizzes and compete with players from around the world.</p>
        </section>

        <div className="text-center card-quiz-day" style={{ padding: '20px' }}>
          <Card.Body>
            <div className='card-title-home'>Daily Quiz <span>5 questions</span></div>
            {/* <Card.Text>Test your knowledge with our daily quiz!</Card.Text> */}
            <div className='topic-card-m'>
              <span>Today Topic</span>
              <h6 className='Quiz-topic'>{quizTitle}</h6>
              {quizPlayedToday ? (
                <Button variant="primary" onClick={() => navigate('/result')}>
                  View Result
                </Button>
              ) : (
                <Button variant="primary" onClick={() => navigate('/quiz')}>
                  Start Quiz
                </Button>
              )}
            </div>
            <div className='top-player'></div>
          </Card.Body>
        </div>

        <AdsBanner />
        
        <Tab.Container defaultActiveKey="yesterday">
  <Nav variant="tabs" className="justify-content-center">
    <Nav.Item>
      <Nav.Link eventKey="yesterday">Daily Champions</Nav.Link>
    </Nav.Item>
    <Nav.Item>
     <Nav.Link eventKey="event">Week’s Leading</Nav.Link>
    </Nav.Item>
  </Nav>

  <Tab.Content>
    <Tab.Pane eventKey="yesterday">
      <Suspense fallback={<Spinner animation="border" />}>
        <YesterdayLeaderboard quizDate={new Date().toISOString().split('T')[0]} />
      </Suspense>
    </Tab.Pane>
    
    <Tab.Pane eventKey="event">
      <Suspense fallback={<Spinner animation="border" />}>
        <EventLeaderboard 
          onClose={() => console.log('Leaderboard closed')} 
          animate={true} 
          playerData={playerData}  // Example player data; replace as necessary
        />
      </Suspense>
    </Tab.Pane>
  </Tab.Content>
</Tab.Container>
      </Container>

      <ProfileModal
        show={showProfileModal}
        handleClose={handleCloseProfileModal}
        playerData={playerData}
        updatePlayerData={handlePlayerDataUpdate}
      />
    </div>
  );
};

export default React.memo(Home);
